/**
 * AdminBackgroundPanel — pestaña del panel Admin para fijar el fondo del
 * canvas de TODOS los usuarios. La imagen elegida se copia a una clave
 * protegida de R2 (prefijo platform/) para que nunca se borre.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileImage, Loader2, RefreshCw, CheckCircle2, Link2, Upload, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function AdminBackgroundPanel() {
  const [current, setCurrent] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    adminService.getPlatformBackground()
      .then((d) => setCurrent(d?.url ? d : null))
      .catch(() => toast.error('No se pudo cargar el fondo actual'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveUrl = async () => {
    const url = urlInput.trim();
    if (!/^https?:\/\/.+\.(png|jpe?g|webp)(\?.*)?$/i.test(url)) {
      toast.error('Pegá la URL de una imagen (png, jpg o webp)');
      return;
    }
    setBusy(true);
    try {
      const value = await adminService.setPlatformBackground(url);
      toast.success('Fondo actualizado para todos los usuarios');
      setUrlInput('');
      setCurrent(value);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo fijar el fondo');
    } finally { setBusy(false); }
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La imagen supera los 8 MB');
      return;
    }
    setBusy(true);
    try {
      const value = await adminService.uploadPlatformBackground(file);
      toast.success('Fondo actualizado para todos los usuarios');
      setCurrent(value);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la imagen');
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-bg-panel" data-testid="admin-bg-panel">
      <div className="admin-bg-header">
        <h3><FileImage size={15} /> Fondo del canvas</h3>
        <button className="admin-refresh" onClick={load} disabled={loading} data-testid="admin-bg-refresh">
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      <p className="admin-tab-help">
        La imagen que elijas se aplica como fondo por defecto del canvas para
        <strong> todos los usuarios</strong> que no hayan personalizado el suyo.
      </p>

      {/* Estado actual */}
      {loading && !current ? (
        <div className="admin-empty"><Loader2 size={20} className="spin" /> Cargando…</div>
      ) : current ? (
        <div className="admin-bg-current" data-testid="admin-bg-current">
          <img src={current.url} alt="Fondo actual del canvas" className="admin-bg-preview" data-testid="admin-bg-preview" />
          <div className="admin-bg-current-meta">
            <span><CheckCircle2 size={12} /> Fondo activo</span>
            <span>Actualizado: {fmtDate(current.updated_at)}</span>
            {current.updated_by && <span>Por: {current.updated_by}</span>}
          </div>
        </div>
      ) : (
        <div className="admin-empty" data-testid="admin-bg-empty">
          <FileImage size={26} /> Sin fondo de plataforma: se usa el video por defecto
        </div>
      )}

      {/* Cambiar desde URL */}
      <div className="admin-bg-actions">
        <div className="admin-bg-input-row">
          <Link2 size={14} />
          <input
            type="url"
            placeholder="https://…/imagen.png"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            data-testid="admin-bg-url-input"
          />
          <button
            className="admin-btn admin-btn-grant"
            onClick={saveUrl}
            disabled={busy || !urlInput.trim()}
            data-testid="admin-bg-save-url"
          >
            {busy ? <Loader2 size={12} className="spin" /> : <CheckCircle2 size={12} />}
            Fijar fondo
          </button>
        </div>
        <div className="admin-bg-input-row">
          <Upload size={14} />
          <span className="admin-bg-or">o subí un archivo desde tu equipo</span>
          <button
            className="admin-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="admin-bg-upload"
          >
            {busy ? <Loader2 size={12} className="spin" /> : <Upload size={12} />}
            Subir imagen
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={pickFile}
            data-testid="admin-bg-file-input"
          />
        </div>
      </div>

      <p className="admin-tab-help">
        <ShieldCheck size={11} /> Al fijarlo, la imagen se <strong>copia a una zona
        protegida de R2</strong> (<code>platform/</code>) que las limpiezas y los
        borrados de usuarios nunca tocan: el fondo no se pierde aunque el asset
        original se elimine.
      </p>
    </div>
  );
}
